import {
  View,
  StyleSheet,
  Text,
  Pressable,
  ActivityIndicator,
} from "react-native";
import WebPage from "@/ui/WebPage";
import { ms } from "@/ui/moderateScale";
import Heading from "@/ui/Heading";
import { useTranslation } from "react-i18next";
import { useUserSlice } from "@/models/useUserSlice";
import { Colors } from "@/constants/Colors";
import { formatDateTime, getBasePath } from "@/models/utils";
import { Folder, Note } from "@/models/types";
import { useRouter } from "@/_router";
import { useUISlice } from "@/models/useUISlice";
import { App } from "@capacitor/app";
import Menu from "@/ui/Menu";
import Icon, { IconType } from "@/ui/Icon";
import { useEffect, useState } from "react";
import { requestReview } from "@/models/useNative";
import { useUpload } from "@/models/useUpload";
import { logFunnelEvent } from "@/fetchData/datadog";
import { Markdown } from "@/ui/Markdown";
import { NoteTabs } from "@/ui/NoteTabs";
import NoteTabTranscript from "@/ui/NoteTabTranscript";
import SelectorButton from "@/ui/SelectorButton";
import { getPlatform } from "@/models/platform";
import AIContentQuiz from "@/ui/AIContentQuiz";
import AIContentFlashcard from "@/ui/AIContentFlashcard";

export default function WebNote() {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const noteId = urlParams.get("id");
  const tabId = urlParams.get("tab");
  const router = useRouter();
  const isMobile = useUISlice((state) => state.device.isMobile);
  const [editable, setEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<
    Note["listAIContent"][number]["type"] | "transcript" | string
  >(tabId || "summary");
  const userTemplates = useUserSlice(
    (state) =>
      state.userData?.listTemplates?.filter(
        (template) => template.type === "user_template"
      ) || []
  );
  const selectedTemplate = userTemplates?.find(
    (template) => template.id === selectedTab
  );
  const note = useUserSlice((state) =>
    state.notes.find((note) => note.id === noteId)
  );
  let summary = note?.listAIContent?.find(
    (content) => content.type === "summary"
  );
  const customAIContent = note?.listAIContent?.find(
    (content) => content?.id === selectedTab
  );

  if (customAIContent) {
    summary = customAIContent;
  }

  useEffect(() => {
    window.scrollTo(0, 0);

    if (note?.status === "summary_completed") {
      requestReview();
    }

    const listenBackButton = () => {
      App.addListener("backButton", () => {
        router.replace(`${getBasePath()}/home`);
      });
    };

    listenBackButton();

    return () => {
      App.removeAllListeners();
    };
  }, []);

  useEffect(() => {
    if (getPlatform() === "web") {
      // Apply the overlay color to the theme-color meta tag for web
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", Colors.white);
      }

      return () => {
        if (metaThemeColor) {
          metaThemeColor.setAttribute("content", "");
        }
      };
    }
  }, []);

  if (!note) return <View />;

  const onCustomSummaryPress = async (
    templateId: string,
    forceRegenerate = false
  ) => {
    setSelectedTab(templateId);

    const existingItem = note?.listAIContent?.find(
      (content) => content?.id === templateId
    );

    if (existingItem && !forceRegenerate) return;

    setLoading(true);

    await useUserSlice
      .getState()
      .note()
      .generateAIContent(noteId, "custom_summary", templateId, forceRegenerate);

    setLoading(false);
  };

  const onSelectAIContent = (callback: (md: string) => void) => {
    const userTemplates = useUserSlice.getState().userData?.listTemplates;
    const listAIContent = useUserSlice
      .getState()
      .notes.find((note) => note.id === noteId)?.listAIContent;

    useUISlice.getState().setAlertSelect({
      title: t("screens.note.selectSection"),
      options: listAIContent.reverse()?.map((content) => {
        if (content.type === "summary") {
          return t("screens.note.summary");
        }

        const targetTemplate = userTemplates?.find(
          (template) => template.id === content.id
        );

        return targetTemplate?.title;
      }),
      onSelect: (index) => {
        const content = listAIContent[index];
        if (!content) return;

        callback(content.markdown);
      },
    });
  };

  if (note.status === "summary_completed") {
    return (
      <>
        <WebPage
          bgColor={Colors.white}
          tab="home"
          screenHeader={{
            right: {
              icon: "moreHorizontal",
              menu: [
                getPlatform() === "web" && {
                  text: t("screens.note.shareNotesAsDocx"),
                  onPress: () =>
                    onSelectAIContent((md) => {
                      useUserSlice
                        .getState()
                        .note()
                        .exportAIContent(noteId, md, "docx");
                    }),
                },
                {
                  text: t("screens.note.shareNotesAsPDF"),
                  onPress: () =>
                    onSelectAIContent((md) => {
                      useUserSlice
                        .getState()
                        .note()
                        .exportAIContent(noteId, md, "pdf");
                    }),
                },
                {
                  text: t("screens.note.shareNotesAsText"),
                  onPress: () =>
                    onSelectAIContent((md) => {
                      useUserSlice
                        .getState()
                        .note()
                        .exportAIContent(noteId, md, "text");
                    }),
                },
                {
                  text: t("screens.note.shareNotesAsMarkdown"),
                  onPress: () =>
                    onSelectAIContent((md) => {
                      useUserSlice
                        .getState()
                        .note()
                        .exportAIContent(noteId, md, "markdown");
                    }),
                },
                {
                  text: t("screens.note.emailNotes"),
                  onPress: () =>
                    useUserSlice.getState().note().emailNote(noteId),
                },
                { divider: true },
                {
                  text: t("screens.note.shareTranscript"),
                  onPress: () =>
                    useUserSlice.getState().note().exportTranscript(noteId),
                },
                { divider: true },
                {
                  text: t("screens.note.rename"),
                  onPress: () => useUserSlice.getState().note().rename(noteId),
                },
                { divider: true },
                {
                  text: t("screens.note.delete"),
                  destructive: true,
                  onPress: () =>
                    useUserSlice
                      .getState()
                      .note()
                      .delete(noteId, () => {
                        router.replace(`${getBasePath()}/home`);
                      }),
                },
              ].filter(Boolean),
            },
          }}
        >
          <View style={{ marginTop: ms(20) }}>
            <WebNote.Header note={note} />
          </View>

          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color={Colors.text} />
            </View>
          )}

          {!loading && (
            <View style={{ flex: 1 }}>
              <NoteTabs
                // onMorePress={() =>
                //   useUISlice.getState().setAlertWidgetAIContentTemplates({
                //     onDeleteCallback: () => {
                //       setSelectedTab("summary");
                //     },
                //   })
                // }
                items={[
                  {
                    title: t("screens.note.summary"),
                    onPress: () => setSelectedTab("summary"),
                    selected: selectedTab === "summary",
                  },
                  {
                    title: t("screens.note.transcript"),
                    onPress: () => setSelectedTab("transcript"),
                    selected: selectedTab === "transcript",
                  },
                  {
                    title: t("screens.note.quiz"),
                    onPress: () => {
                      setSelectedTab("quiz");
                      useUserSlice
                        .getState()
                        .note()
                        .generateAIContent(noteId, "quiz", selectedTab);
                    },
                    selected: selectedTab === "quiz",
                  },
                  {
                    title: t("screens.note.flashcards"),
                    onPress: () => {
                      setSelectedTab("flashcards");
                      useUserSlice
                        .getState()
                        .note()
                        .generateAIContent(noteId, "flashcards", selectedTab);
                    },
                    selected: selectedTab === "flashcards",
                  },
                  {
                    title: t("screens.note.askAI"),
                    onPress: () =>
                      useUISlice.getState().setAlertWidgetChat({ note }),
                  },
                  // ...userTemplates?.map((template) => ({
                  //   title: template.title,
                  //   onPress: () => onCustomSummaryPress(template.id),
                  //   selected: selectedTab === template.id,
                  // })),
                ]}
              />

              <View style={{ height: ms(32) }} />

              {selectedTab === "summary" && (
                <View style={{ flex: 1 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Heading
                      text={t("screens.note.details")}
                      style={{
                        fontSize: ms(12),
                        fontWeight: "300",
                        color: Colors.text,
                        textTransform: "uppercase",
                        letterSpacing: ms(0.5),
                      }}
                    />

                    {editable && (
                      <Text
                        onPress={() => setEditable(!editable)}
                        style={{
                          fontSize: ms(14),
                          color: Colors.accent,
                          fontWeight: "500",
                          paddingRight: ms(4),
                        }}
                      >
                        {t("screens.note.save")}
                      </Text>
                    )}
                    {!editable && (
                      <Menu
                        key={selectedTab}
                        position="bottom right"
                        actions={[
                          {
                            text: t("screens.note.edit"),
                            onPress: () => setEditable(!editable),
                          },
                          {
                            text: t("screens.note.editWithAI"),
                            onPress: () =>
                              useUISlice
                                .getState()
                                .setAlertWidgetAIContentEditWithAI({
                                  noteId: note.id,
                                  title:
                                    selectedTemplate?.title ||
                                    t("screens.note.summary"),
                                  userTemplateId: selectedTab,
                                  callback: () => {
                                    onCustomSummaryPress(selectedTab, true);
                                  },
                                }),
                          },
                        ]}
                        actionComponent={
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: ms(2),
                            }}
                          >
                            <Text
                              style={{
                                fontSize: ms(14),
                                color: Colors.accent,
                                fontWeight: "500",
                              }}
                            >
                              {t("screens.note.edit")}
                            </Text>
                            <Icon
                              icon="caretDownFilled"
                              style={{
                                tintColor: Colors.accent,
                                width: ms(20),
                                height: ms(20),
                              }}
                            />
                          </View>
                        }
                      />
                    )}
                  </View>

                  <View
                    key={editable ? "editable" : "non-editable"}
                    style={styles.summaryContainer}
                  >
                    <Markdown
                      key={customAIContent?.updatedAt || customAIContent?.id}
                      value={summary?.markdown}
                      editable={editable}
                      backgroundColor={isMobile ? Colors.white : undefined}
                      onBlur={(value) => {
                        useUserSlice
                          .getState()
                          .note()
                          .editAIContent(note.id, summary?.id, {
                            markdown: value.trim(),
                          });
                      }}
                    />
                  </View>
                </View>
              )}

              {selectedTab === "transcript" && (
                <NoteTabTranscript note={note} />
              )}

              {selectedTab === "quiz" && <AIContentQuiz quiz={summary?.quiz} />}

              {selectedTab === "flashcards" && (
                <AIContentFlashcard flashcards={summary?.flashcards || []} />
              )}

              <View style={{ height: ms(100) }} />
            </View>
          )}
        </WebPage>
      </>
    );
  }

  return <WebNote.IncompleteNoteViewer note={note} />;
}

WebNote.Header = ({
  note,
  screen,
}: {
  note: Note;
  screen?: "transcript" | "summary";
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const folders = useUserSlice((state) => state.folders);
  const selectedFolder = useUserSlice((state) =>
    state.folders.find((f) => f?.listNotesIds?.includes(note.id))
  );
  const isMobile = useUISlice((state) => state.device.isMobile);

  let lastScreenText: string;

  switch (screen) {
    case "transcript":
      lastScreenText = t("screens.note.transcript");
      break;
    case "summary":
      lastScreenText = t("screens.note.summary");
      break;
  }

  const onSelectFolder = (folder?: Folder) => {
    if (!folder && selectedFolder) {
      useUserSlice.getState().folder().removeNote(selectedFolder.id, note.id);
      return;
    }

    if (folder) {
      useUserSlice.getState().folder().addNote(folder.id, note.id);
    }
  };

  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: ms(12),
        }}
      >
        {!isMobile && (
          <WebPage.ListMenu
            items={[
              {
                text: t("screens.note.home"),
                onPress: () => router.replace(`${getBasePath()}/home`),
              },
              {
                text: t("screens.note.noteDetails"),
                onPress: () =>
                  router.replace(`${getBasePath()}/note?id=${note.id}`),
              },
              screen && { text: lastScreenText, onPress: () => {} },
            ].filter(Boolean)}
          />
        )}
      </View>
      <Pressable onPress={() => useUserSlice.getState().note().rename(note.id)}>
        <Heading text={note?.name} style={{ marginBottom: ms(6) }} />
      </Pressable>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: ms(12),
          marginBottom: ms(12),
          marginTop: ms(4),
        }}
      >
        <Menu
          position="bottom left"
          key={
            selectedFolder
              ? `${selectedFolder?.id}-${selectedFolder?.name}`
              : folders.map((folder) => folder.name).join("-")
          }
          actions={[
            {
              icon: "folder" as IconType,
              text: t("screens.home.allNotes"),
              onPress: () => onSelectFolder(undefined),
              notificationText: selectedFolder ? undefined : "✓",
              iconStyle: { width: ms(18), height: ms(18) },
            },
            ...folders.map((folder) => ({
              icon: "folder" as IconType,
              text: folder.name,
              iconStyle: { width: ms(18), height: ms(18) },
              onPress: () => onSelectFolder(folder),
              notificationText:
                selectedFolder?.id === folder.id ? "✓" : undefined,
            })),
            { divider: true },
            {
              icon: "plus" as IconType,
              text: t("screens.home.newFolder"),
              iconStyle: {
                width: ms(24),
                height: ms(24),
                tintColor: Colors.text,
              },
              onPress: () => useUserSlice.getState().folder().create(),
            },
          ].filter(Boolean)}
          actionComponent={
            <SelectorButton
              text={
                selectedFolder
                  ? `📂 ${selectedFolder.name}`
                  : t("screens.note.addToFolder")
              }
              rightIcon="caretDownFilled"
            />
          }
        />
        <Text style={styles.dateText}>{formatDateTime(note.createdAt)}</Text>
      </View>
    </View>
  );
};

WebNote.IncompleteNoteViewer = ({ note }: { note: Note }) => {
  const { t } = useTranslation();
  const { uploadFile, uploadProgress, setUploadParams } = useUpload({
    maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB
  });
  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const progress = recordingProgress || uploadProgress;

  useEffect(() => {
    const onUploadStarted = async (): Promise<string | undefined> => {
      const params = useUISlice.getState().noteScreenData?.upload_started;
      const isLiveRecording = !params?.file && note.type === "voice_recording";

      if (isLiveRecording) {
        useUserSlice.getState().note().onLiveRecordingCompleted(note.id);

        let interval = setInterval(() => {
          useUserSlice
            .getState()
            .note()
            .liveRecordingChunk({
              action: "status",
              noteId: note.id,
              callback: async (status) => {
                if (!status) return;

                if (status?.uploadProgress) {
                  setRecordingProgress(status.uploadProgress);
                }

                if (status?.fileUrl) {
                  clearInterval(interval);
                }
              },
            });
        }, 1000);

        return;
      }

      if (!params) return;

      const fileExtension = params?.file?.name?.split(".").pop() || "";
      setUploadParams({ fileType: params?.file?.type, fileExtension });

      logFunnelEvent("[2] - upload started");

      const uploadResponse = await uploadFile(params?.file);

      if (!uploadResponse.success) {
        useUserSlice.getState().note().update(note.id, {
          status: "upload_failed",
        });
        return;
      }

      await useUserSlice
        .getState()
        .note()
        .onUploadCompleted(note.id, uploadResponse.downloadUrl);
    };

    switch (note.status) {
      case "upload_started":
        onUploadStarted();
        break;
      case "upload_completed":
        useUserSlice.getState().note().status(note.id);
        break;
      case "transcription_started":
        useUserSlice.getState().note().status(note.id);
        break;
      case "transcription_completed":
        useUserSlice.getState().note().generateAIContent(note.id, "summary");
        break;
    }
  }, [note?.status]);

  if (note?.status?.endsWith("failed")) {
    return (
      <WebPage tab="home">
        <View style={{ flex: 1, alignItems: "center", marginTop: ms(24) }}>
          <Text
            style={{
              fontSize: ms(16),
              fontWeight: "500",
              marginBottom: ms(12),
              color: Colors.error,
            }}
          >
            {t("screens.note.transcribingNoteFailed")}
          </Text>
        </View>
      </WebPage>
    );
  }

  const statusItemsMappers: {
    [key in Exclude<
      Note["status"],
      | "upload_failed"
      | "transcription_failed"
      | "summary_failed"
      | "summary_completed"
    >]: string[];
  } = {
    upload_started: [
      t("screens.note.uploading", { progress: progress.toFixed(2) }),
    ],
    upload_completed: [
      t("screens.note.uploadCompleted"),
      t("screens.note.transcribing"),
    ],
    transcription_started: [
      t("screens.note.uploadCompleted"),
      t("screens.note.transcribing"),
    ],
    transcription_completed: [
      t("screens.note.uploadCompleted"),
      t("screens.note.transcriptionCompleted"),
      t("screens.note.creatingNote"),
    ],
    summary_started: [
      t("screens.note.uploadCompleted"),
      t("screens.note.transcriptionCompleted"),
      t("screens.note.creatingNote"),
    ],
  };
  const statusItems = statusItemsMappers[note.status];

  return (
    <WebPage tab="home">
      <View
        style={{
          backgroundColor: Colors.white,
          flex: 1,
          marginTop: ms(24),
          padding: ms(16),
          borderRadius: ms(12),
          gap: ms(12),
        }}
      >
        {statusItems.map((item: string, index: number) => (
          <View
            key={index}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: ms(12),
            }}
          >
            {index + 1 < statusItems.length && (
              <Icon
                icon="tickCircle"
                style={{
                  width: ms(22),
                  height: ms(22),
                  tintColor: Colors.success,
                }}
              />
            )}
            {index + 1 === statusItems.length && (
              <ActivityIndicator size="small" color={Colors.subtitle} />
            )}
            <Text style={{ color: Colors.text, fontSize: ms(16) }}>{item}</Text>
          </View>
        ))}
      </View>
    </WebPage>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  dateText: {
    color: Colors.subtitle,
    fontSize: ms(12),
    fontWeight: "300",
  },
  summaryContainer: {
    flexGrow: 1,
    paddingTop: ms(0),
    borderRadius: ms(4),
    marginBottom: ms(24),
  },
  editButton: {
    width: ms(32),
    height: ms(32),
    alignSelf: "flex-end",
    backgroundColor: Colors.text + "10",
    borderRadius: ms(16),
    padding: ms(8),
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    tintColor: Colors.accent,
    width: ms(16),
    height: ms(16),
  },
  loadingContainer: {
    flex: 1,
    minHeight: ms(600),
    justifyContent: "center",
  },
});
